import React, { useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { css } from '@emotion/core'
import { FrontSupport } from '@/components/organisms/self-checkin/complete/front-support'
import { FrontSupportWithPrinter } from '@/components/organisms/self-checkin/complete/front-support-with-printer'
import { SelfCheckinBasicInfoPluginType } from '@/models/self-checkin/results'
import { AssignRoomWithPrinter } from '@/components/organisms/self-checkin/complete/assign-room-with-printer'
import { AssignRoom } from '@/components/organisms/self-checkin/complete/assign-room'
import { selfCheckinFetchGuestRoomAssign } from '@/apis/aipass'
import { IssueKeyWithPrinter } from '@/components/organisms/self-checkin/complete/issue-key-with-printer'
import { checkinEposPrinterIdStorage, serialNumberStorageKey } from '@/constants/self-checkin-device-storage'
import { useSelfCheckInState } from '@/hooks/use-self-check-in-state'
import * as Vescajs from '@/apis/vescajs'
import { SelfCheckinLayout } from '@/components/layouts/self-checkin-layout'
import { CASH_CHANGER, CREDIT_CARD, PaymentMethod } from '@/models/payment-method'
import { SelectedReservationAndRooms } from '@/models/self-checkin'
import { HotelNoticePopup } from '@/components/organisms/hotel-notice-popup'
import type { MultilingualText } from '@/models/survey/types'
import { useTranslation } from 'react-i18next'

export type SelfCheckinCompleteState = {
  hotelId: string
  checkinId: string
  isFrontDescGuidance?: boolean
  basicInfo: SelfCheckinBasicInfoPluginType['basicInfo']
  paymentMethod?: PaymentMethod
  confirmUsageTotalAmount: number
  payment?: {
    paymentAmount: number
    totalAmountAt10Per: number
    totalAmountAt8Per: number
    totalTaxAt10Per: number
    totalTaxAt8Per: number
    receiptName: string | undefined
    brandName: string
    salesSlip: Vescajs.CreditSalesSlip | Vescajs.QRSalesSlip | undefined
    deviceType: Vescajs.DeviceType | undefined
  }
  hasAnyGuestCheckedIn: boolean
  selectedReservationAndRooms: SelectedReservationAndRooms[]
  // 予約を選ばずにチェックインした場合（その場で予約を作成）は UUID を持たないため undefined
  uploadReservationUuid?: string
  checkinGuidanceMemo?: Partial<Record<keyof MultilingualText, string[]>>
}

export const SelfCheckinComplete: React.FC<{}> = () => {
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const { state, saveState, stateErrorHandling } = useSelfCheckInState()
  const [isPageInit, setIsPageInit] = useState<boolean>(false)
  const [existsSmartKeyRoom, setExistsSmartKeyRoom] = useState<boolean>(false)
  const [existsUndefinedKeyRoom, setExistsUndefinedKeyRoom] = useState<boolean>(false)
  const isSettingCardReader = !!localStorage.getItem(serialNumberStorageKey)
  const existsPrinter: boolean = !!localStorage.getItem(checkinEposPrinterIdStorage)

  const goToTopPage = () => {
    history.push({ pathname: `/self-checkin`, search: `?hotelId=${state.hotelId}` })
  }

  const initPageStatus = async () => {
    if (!stateErrorHandling(state, 'complete')) {
      return
    }
    const roomInfoRes = await selfCheckinFetchGuestRoomAssign({
      hotelId: state.hotelId,
      reservationIdWithAssignIds: state.complete.selectedReservationAndRooms,
    })
    const flatRoomInfo: any = Object.values(roomInfoRes).flat()
    setExistsSmartKeyRoom(flatRoomInfo.some(room => room.keyNumber && room.keyNumber !== '-'))
    setExistsUndefinedKeyRoom(flatRoomInfo.some(room => !room.keyNumber || room.keyNumber === '-'))

    setIsPageInit(true)
  }

  const hasUnpaid: boolean = useMemo(() => {
    return ![CREDIT_CARD, CASH_CHANGER].includes(state.complete?.paymentMethod || '') && (state.complete?.confirmUsageTotalAmount || 0) > 0
  }, [])

  useEffect(() => {
    const hasNoticeMessages = !!state.complete?.checkinGuidanceMemo?.[i18n.language]?.length
    const timeoutMs = hasNoticeMessages ? 90000 : 60000
    const timeId = setTimeout(() => goToTopPage(), timeoutMs)
    return () => clearTimeout(timeId)
  }, [])

  useEffect(() => {
    window.scrollTo(0, 0)
    initPageStatus()
  }, [])

  if (!isPageInit) {
    return <></>
  }

  if (existsPrinter) {
    return (
      <SelfCheckinLayout state={state} fullscreen>
        <HotelNoticePopup messages={state.complete!.checkinGuidanceMemo?.[i18n.language]} />
        <div css={containerStyle}>
          {!hasUnpaid && existsSmartKeyRoom && !existsUndefinedKeyRoom ? (
            <AssignRoomWithPrinter
              onClickTop={goToTopPage}
              onPrint={() => saveState({ ...state, completeReceiptHandled: true })}
              hotelId={state.hotelId}
              checkinId={state.complete!.checkinId}
              basicInfo={state.setting.basicInfo}
              state={state.complete!}
              completeReceiptHandled={state.completeReceiptHandled || false}
            />
          ) : !hasUnpaid && isSettingCardReader ? (
            <IssueKeyWithPrinter
              onClickTop={goToTopPage}
              onPrint={() => saveState({ ...state, completeReceiptHandled: true })}
              hotelId={state.hotelId}
              checkinId={state.complete!.checkinId}
              basicInfo={state.setting.basicInfo}
              state={state.complete!}
              completeReceiptHandled={state.completeReceiptHandled || false}
            />
          ) : (
            <FrontSupportWithPrinter
              onClickTop={goToTopPage}
              onPrint={() => saveState({ ...state, completeReceiptHandled: true })}
              state={state.complete!}
              basicInfo={state.setting.basicInfo}
              completeReceiptHandled={state.completeReceiptHandled || false}
            />
          )}
        </div>
      </SelfCheckinLayout>
    )
  }
  const showAssignRoom = !state.setting.accommodation.isFrontDescGuidance || existsSmartKeyRoom

  return (
    <SelfCheckinLayout state={state} fullscreen header={showAssignRoom ? { title: t('Room information') } : undefined}>
      <HotelNoticePopup messages={state.complete!.checkinGuidanceMemo?.[i18n.language]} />
      <div css={containerStyle}>
        {showAssignRoom ? (
          <AssignRoom
            onClickTop={goToTopPage}
            hotelId={state.hotelId}
            checkinId={state.complete!.checkinId}
            basicInfo={state.setting.basicInfo}
            state={state.complete!}
          />
        ) : (
          <FrontSupport onClickTop={goToTopPage} />
        )}
      </div>
    </SelfCheckinLayout>
  )
}

const containerStyle = css({
  width: '100%',
  position: 'relative',
  '*': { fontFamily: 'Noto Sans JP' },
})
